import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Gavel,
  PieChart,
  ArrowRight,
  CheckCircle,
  FileText,
  Scale,
  Clock,
  Award,
  Ban,
  Loader2,
  Download,
  BarChart3,
  Calendar,
  AlertTriangle,
  Target,
  Upload,
  User,
  Building,
  Phone,
  Mail,
  Trash2,
  Eye,
} from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { useNavigate } from "react-router-dom";
import {
  CasePrediction as CasePredictionType,
  LegalPrecedent,
  analyzeCase,
  createCasePrediction,
  getUserCasePredictions,
  deleteCasePrediction,
  getPrecedent,
} from "@/services/predictionService";

import { useDocumentViewer } from "@/contexts/DocumentViewerContext";

export function CasePrediction() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { openDocument } = useDocumentViewer();

  const [activeTab, setActiveTab] = useState("new-analysis");
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [previousAnalysis, setPreviousAnalysis] = useState<
    CasePredictionType[]
  >([]);
  const [precedents, setPrecedents] = useState<
    { id: number; expediente: string }[]
  >([
    { id: 1, expediente: "" },
    { id: 2, expediente: "" },
  ]);
  const [showResults, setShowResults] = useState(false);
  const [activeAnalysis, setActiveAnalysis] =
    useState<CasePredictionType | null>(null);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);

  // Form state
  const [formData, setFormData] = useState<Partial<CasePredictionType>>({
    case_type: "",
    jurisdiction: "",
    court: "",
    claim_amount: 0,
    case_summary: "",
    key_arguments: "",
    precedents: [],
  });

  // Additional form fields
  const [additionalData, setAdditionalData] = useState({
    case_number: "",
    filing_date: "",
    judge_name: "",
    judge_specialty: "",
    plaintiff_lawyer_name: "",
    plaintiff_lawyer_firm: "",
    plaintiff_lawyer_phone: "",
    plaintiff_lawyer_email: "",
    defendant_lawyer_name: "",
    defendant_lawyer_firm: "",
    defendant_lawyer_phone: "",
    defendant_lawyer_email: "",
    plaintiff_name: "",
    defendant_name: "",
    case_priority: "",
    case_status: "",
    estimated_duration: "",
    legal_basis: "",
    procedural_stage: "",
  });

  // File upload state
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [filePreview, setFilePreview] = useState<{ [key: string]: string }>({});

  // Load previous analysis on tab change
  useEffect(() => {
    if (activeTab === "previous") {
      loadPreviousAnalysis();
    }
  }, [activeTab]);

  const loadPreviousAnalysis = async () => {
    setIsLoading(true);
    const predictions = await getUserCasePredictions();
    setPreviousAnalysis(predictions);
    setIsLoading(false);
  };

  const handleInputChange = (
    field: keyof CasePredictionType,
    value: string | number | null
  ) => {
    setFormData({
      ...formData,
      [field]: value,
    });
  };

  const handleAdditionalDataChange = (field: string, value: string) => {
    setAdditionalData({
      ...additionalData,
      [field]: value,
    });
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    const newFiles = [...uploadedFiles, ...files];
    setUploadedFiles(newFiles);

    // Create preview URLs for images and PDFs
    files.forEach((file) => {
      if (file.type.startsWith("image/") || file.type === "application/pdf") {
        const reader = new FileReader();
        reader.onload = (e) => {
          setFilePreview((prev) => ({
            ...prev,
            [file.name]: e.target?.result as string,
          }));
        };
        reader.readAsDataURL(file);
      }
    });

    toast({
      title: "Archivos cargados",
      description: `${files.length} archivo(s) cargado(s) exitosamente`,
    });
  };

  const removeFile = (fileName: string) => {
    setUploadedFiles((prev) => prev.filter((file) => file.name !== fileName));
    setFilePreview((prev) => {
      const newPreview = { ...prev };
      delete newPreview[fileName];
      return newPreview;
    });
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const handlePrecedentChange = async (index: number, expediente: string) => {
    const newPrecedents = [...precedents];
    newPrecedents[index].expediente = expediente;
    setPrecedents(newPrecedents);

    const precedentList = newPrecedents
      .filter((p) => p.expediente.trim() !== "")
      .map((p) => ({ expediente: p.expediente }));

    setFormData({
      ...formData,
      precedents: precedentList,
    });
  };

  const addPrecedentField = () => {
    setPrecedents([
      ...precedents,
      { id: precedents.length + 1, expediente: "" },
    ]);
  };

  const handleAnalyzeCase = async () => {
    if (
      !formData.case_type ||
      !formData.jurisdiction ||
      !formData.case_summary ||
      formData.claim_amount === undefined ||
      !additionalData.case_number ||
      !additionalData.judge_name ||
      !additionalData.plaintiff_lawyer_name ||
      !additionalData.defendant_lawyer_name
    ) {
      toast({
        title: "Datos incompletos",
        description:
          "Por favor complete todos los campos obligatorios: tipo de caso, jurisdicción, resumen, número de radicado, juez asignado, abogado demandante y abogado defensor.",
        variant: "destructive",
      });
      return;
    }

    setIsAnalyzing(true);

    try {
      const prediction = await analyzeCase(formData);
      const result = await createCasePrediction({
        ...prediction,
        user_id: "demo_user", // Using demo user since auth is removed
      });

      if (result) {
        setActiveAnalysis(result);
        setShowResults(true);

        setTimeout(() => {
          const resultsElement = document.getElementById("results-card");
          if (resultsElement) {
            resultsElement.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          }
        }, 100);
      }
    } catch (error) {
      console.error("Error analyzing case:", error);
      toast({
        title: "Error",
        description: "No se pudo completar el análisis. Intente nuevamente.",
        variant: "destructive",
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleViewAnalysis = (analysis: CasePredictionType) => {
    setActiveAnalysis(analysis);
    setShowResults(true);
    setActiveTab("new-analysis");

    setFormData({
      case_type: analysis.case_type,
      jurisdiction: analysis.jurisdiction,
      court: analysis.court,
      claim_amount: analysis.claim_amount,
      case_summary: analysis.case_summary,
      key_arguments: analysis.key_arguments,
      precedents: analysis.precedents,
    });

    if (analysis.precedents && analysis.precedents.length > 0) {
      const newPrecedents = analysis.precedents.map((p, index) => ({
        id: index + 1,
        expediente: p.expediente,
      }));

      while (newPrecedents.length < 2) {
        newPrecedents.push({ id: newPrecedents.length + 1, expediente: "" });
      }

      setPrecedents(newPrecedents);
    }
  };

  const handleDeleteAnalysis = async (id: string) => {
    if (window.confirm("¿Está seguro que desea eliminar este análisis?")) {
      const success = await deleteCasePrediction(id);
      if (success) {
        loadPreviousAnalysis();
      }
    }
  };

  const handleCheckPrecedent = async (expediente: string) => {
    if (!expediente.trim()) {
      toast({
        title: "Expediente vacío",
        description: "Por favor ingrese un número de expediente.",
        variant: "destructive",
      });
      return;
    }

    const precedent = await getPrecedent(expediente);

    if (precedent) {
      toast({
        title: `Expediente ${expediente}`,
        description: `${precedent.summary} - ${precedent.outcome}`,
      });
    } else {
      toast({
        title: "Expediente no encontrado",
        description:
          "No se encontró información sobre el expediente ingresado.",
        variant: "destructive",
      });
    }
  };

  const handleGenerateReport = () => {
    if (!activeAnalysis || !activeAnalysis.id) {
      toast({
        title: "No hay análisis activo",
        description: "Primero debe analizar un caso para generar un informe",
        variant: "destructive",
      });
      return;
    }
    // Navigate to the new report page
    navigate(
      `/dashboard/agents/litigation-predictor/case-prediction/report/${activeAnalysis.id}`
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <Scale className="h-8 w-8 text-primary" />
          Análisis Predictivo de Caso Legal
        </h1>
        <p className="text-muted-foreground mt-1">
          Sistema de Inteligencia Artificial para el Sistema Judicial Colombiano
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="new-analysis" className="gap-2">
            <Gavel size={16} /> Nuevo análisis
          </TabsTrigger>
          <TabsTrigger value="previous" className="gap-2">
            <Clock size={16} /> Análisis previos
          </TabsTrigger>
        </TabsList>

        <TabsContent value="new-analysis">
          <div className="mt-6 grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              {/* Case Information Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-xl flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Información del Caso
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Basic Case Information */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-primary">
                      Datos Básicos del Caso
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label
                          htmlFor="case-number"
                          className="text-sm font-medium">
                          Número de Radicado *
                        </Label>
                        <Input
                          id="case-number"
                          placeholder="11001-31-03-001-2024-00567-00"
                          className="font-mono text-sm"
                          value={additionalData.case_number}
                          onChange={(e) =>
                            handleAdditionalDataChange(
                              "case_number",
                              e.target.value
                            )
                          }
                        />
                      </div>

                      <div className="space-y-2">
                        <Label
                          htmlFor="case-type"
                          className="text-sm font-medium">
                          Tipo de Proceso *
                        </Label>
                        <Select
                          value={formData.case_type}
                          onValueChange={(value) =>
                            handleInputChange("case_type", value)
                          }>
                          <SelectTrigger id="case-type">
                            <SelectValue placeholder="Seleccione tipo de proceso" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectGroup>
                              <SelectItem value="civil">
                                Ejecutivo Singular
                              </SelectItem>
                              <SelectItem value="labor">
                                Laboral Ordinario
                              </SelectItem>
                              <SelectItem value="corporate">
                                Mercantil
                              </SelectItem>
                              <SelectItem value="family">Familiar</SelectItem>
                              <SelectItem value="administrative">
                                Administrativo
                              </SelectItem>
                              <SelectItem value="penal">Penal</SelectItem>
                              <SelectItem value="contencioso">
                                Contencioso Administrativo
                              </SelectItem>
                            </SelectGroup>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="court" className="text-sm font-medium">
                          Juzgado *
                        </Label>
                        <Select
                          value={formData.court}
                          onValueChange={(value) =>
                            handleInputChange("court", value)
                          }>
                          <SelectTrigger id="court">
                            <SelectValue placeholder="Seleccione juzgado" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectGroup>
                              <SelectItem value="civil-bogota">
                                Juzgado 15 Civil del Circuito de Bogotá
                              </SelectItem>
                              <SelectItem value="laboral-bogota">
                                Juzgado Laboral del Circuito de Bogotá
                              </SelectItem>
                              <SelectItem value="civil-medellin">
                                Juzgado Civil del Circuito de Medellín
                              </SelectItem>
                              <SelectItem value="civil-cali">
                                Juzgado Civil del Circuito de Cali
                              </SelectItem>
                              <SelectItem value="civil-barranquilla">
                                Juzgado Civil del Circuito de Barranquilla
                              </SelectItem>
                            </SelectGroup>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label
                          htmlFor="jurisdiction"
                          className="text-sm font-medium">
                          Jurisdicción *
                        </Label>
                        <Select
                          value={formData.jurisdiction}
                          onValueChange={(value) =>
                            handleInputChange("jurisdiction", value)
                          }>
                          <SelectTrigger id="jurisdiction">
                            <SelectValue placeholder="Seleccione jurisdicción" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectGroup>
                              <SelectItem value="bogota">
                                Bogotá D.C.
                              </SelectItem>
                              <SelectItem value="medellin">Medellín</SelectItem>
                              <SelectItem value="cali">Cali</SelectItem>
                              <SelectItem value="barranquilla">
                                Barranquilla
                              </SelectItem>
                              <SelectItem value="bucaramanga">
                                Bucaramanga
                              </SelectItem>
                              <SelectItem value="manizales">
                                Manizales
                              </SelectItem>
                            </SelectGroup>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label
                          htmlFor="filing-date"
                          className="text-sm font-medium">
                          Fecha de Radicación
                        </Label>
                        <Input
                          id="filing-date"
                          type="date"
                          value={additionalData.filing_date}
                          onChange={(e) =>
                            handleAdditionalDataChange(
                              "filing_date",
                              e.target.value
                            )
                          }
                        />
                      </div>

                      <div className="space-y-2">
                        <Label
                          htmlFor="claim-amount"
                          className="text-sm font-medium">
                          Cuantía (COP) *
                        </Label>
                        <Input
                          id="claim-amount"
                          placeholder="50.000.000"
                          type="text"
                          value={formData.claim_amount?.toLocaleString() || ""}
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^\d]/g, "");
                            handleInputChange("claim_amount", Number(value));
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Judge Information */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-primary flex items-center gap-2">
                      <Scale className="h-5 w-5" />
                      Información del Juez
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label
                          htmlFor="judge-name"
                          className="text-sm font-medium">
                          Nombre del Juez *
                        </Label>
                        <Input
                          id="judge-name"
                          placeholder="Dr. María Elena Rodríguez"
                          value={additionalData.judge_name}
                          onChange={(e) =>
                            handleAdditionalDataChange(
                              "judge_name",
                              e.target.value
                            )
                          }
                        />
                      </div>

                      <div className="space-y-2">
                        <Label
                          htmlFor="judge-specialty"
                          className="text-sm font-medium">
                          Especialidad del Juez
                        </Label>
                        <Select
                          value={additionalData.judge_specialty}
                          onValueChange={(value) =>
                            handleAdditionalDataChange("judge_specialty", value)
                          }>
                          <SelectTrigger id="judge-specialty">
                            <SelectValue placeholder="Seleccione especialidad" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectGroup>
                              <SelectItem value="civil">Civil</SelectItem>
                              <SelectItem value="laboral">Laboral</SelectItem>
                              <SelectItem value="penal">Penal</SelectItem>
                              <SelectItem value="familia">Familia</SelectItem>
                              <SelectItem value="administrativo">
                                Administrativo
                              </SelectItem>
                              <SelectItem value="mercantil">
                                Mercantil
                              </SelectItem>
                            </SelectGroup>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* Plaintiff Information */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-primary flex items-center gap-2">
                      <User className="h-5 w-5" />
                      Información del Demandante
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label
                          htmlFor="plaintiff-name"
                          className="text-sm font-medium">
                          Nombre del Demandante
                        </Label>
                        <Input
                          id="plaintiff-name"
                          placeholder="Juan Pérez"
                          value={additionalData.plaintiff_name}
                          onChange={(e) =>
                            handleAdditionalDataChange(
                              "plaintiff_name",
                              e.target.value
                            )
                          }
                        />
                      </div>

                      <div className="space-y-2">
                        <Label
                          htmlFor="plaintiff-lawyer-name"
                          className="text-sm font-medium">
                          Abogado Demandante *
                        </Label>
                        <Input
                          id="plaintiff-lawyer-name"
                          placeholder="Dr. Carlos Mendoza"
                          value={additionalData.plaintiff_lawyer_name}
                          onChange={(e) =>
                            handleAdditionalDataChange(
                              "plaintiff_lawyer_name",
                              e.target.value
                            )
                          }
                        />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label
                          htmlFor="plaintiff-lawyer-firm"
                          className="text-sm font-medium">
                          Despacho/Estudio Jurídico
                        </Label>
                        <Input
                          id="plaintiff-lawyer-firm"
                          placeholder="Estudio Jurídico Mendoza & Asociados"
                          value={additionalData.plaintiff_lawyer_firm}
                          onChange={(e) =>
                            handleAdditionalDataChange(
                              "plaintiff_lawyer_firm",
                              e.target.value
                            )
                          }
                        />
                      </div>

                      <div className="space-y-2">
                        <Label
                          htmlFor="plaintiff-lawyer-phone"
                          className="text-sm font-medium">
                          Teléfono
                        </Label>
                        <Input
                          id="plaintiff-lawyer-phone"
                          placeholder="+57 300 123 4567"
                          value={additionalData.plaintiff_lawyer_phone}
                          onChange={(e) =>
                            handleAdditionalDataChange(
                              "plaintiff_lawyer_phone",
                              e.target.value
                            )
                          }
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label
                        htmlFor="plaintiff-lawyer-email"
                        className="text-sm font-medium">
                        Email
                      </Label>
                      <Input
                        id="plaintiff-lawyer-email"
                        type="email"
                        placeholder="carlos.mendoza@estudio.com"
                        value={additionalData.plaintiff_lawyer_email}
                        onChange={(e) =>
                          handleAdditionalDataChange(
                            "plaintiff_lawyer_email",
                            e.target.value
                          )
                        }
                      />
                    </div>
                  </div>

                  {/* Defendant Information */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-primary flex items-center gap-2">
                      <Building className="h-5 w-5" />
                      Información del Demandado
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label
                          htmlFor="defendant-name"
                          className="text-sm font-medium">
                          Nombre del Demandado
                        </Label>
                        <Input
                          id="defendant-name"
                          placeholder="Empresa ABC S.A.S."
                          value={additionalData.defendant_name}
                          onChange={(e) =>
                            handleAdditionalDataChange(
                              "defendant_name",
                              e.target.value
                            )
                          }
                        />
                      </div>

                      <div className="space-y-2">
                        <Label
                          htmlFor="defendant-lawyer-name"
                          className="text-sm font-medium">
                          Abogado Defensor *
                        </Label>
                        <Input
                          id="defendant-lawyer-name"
                          placeholder="Dr. Ana García"
                          value={additionalData.defendant_lawyer_name}
                          onChange={(e) =>
                            handleAdditionalDataChange(
                              "defendant_lawyer_name",
                              e.target.value
                            )
                          }
                        />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label
                          htmlFor="defendant-lawyer-firm"
                          className="text-sm font-medium">
                          Despacho/Estudio Jurídico
                        </Label>
                        <Input
                          id="defendant-lawyer-firm"
                          placeholder="García & Asociados"
                          value={additionalData.defendant_lawyer_firm}
                          onChange={(e) =>
                            handleAdditionalDataChange(
                              "defendant_lawyer_firm",
                              e.target.value
                            )
                          }
                        />
                      </div>

                      <div className="space-y-2">
                        <Label
                          htmlFor="defendant-lawyer-phone"
                          className="text-sm font-medium">
                          Teléfono
                        </Label>
                        <Input
                          id="defendant-lawyer-phone"
                          placeholder="+57 300 987 6543"
                          value={additionalData.defendant_lawyer_phone}
                          onChange={(e) =>
                            handleAdditionalDataChange(
                              "defendant_lawyer_phone",
                              e.target.value
                            )
                          }
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label
                        htmlFor="defendant-lawyer-email"
                        className="text-sm font-medium">
                        Email
                      </Label>
                      <Input
                        id="defendant-lawyer-email"
                        type="email"
                        placeholder="ana.garcia@estudio.com"
                        value={additionalData.defendant_lawyer_email}
                        onChange={(e) =>
                          handleAdditionalDataChange(
                            "defendant_lawyer_email",
                            e.target.value
                          )
                        }
                      />
                    </div>
                  </div>

                  {/* Case Details */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-primary">
                      Detalles del Caso
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label
                          htmlFor="case-priority"
                          className="text-sm font-medium">
                          Prioridad del Caso
                        </Label>
                        <Select
                          value={additionalData.case_priority}
                          onValueChange={(value) =>
                            handleAdditionalDataChange("case_priority", value)
                          }>
                          <SelectTrigger id="case-priority">
                            <SelectValue placeholder="Seleccione prioridad" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectGroup>
                              <SelectItem value="urgente">Urgente</SelectItem>
                              <SelectItem value="alta">Alta</SelectItem>
                              <SelectItem value="media">Media</SelectItem>
                              <SelectItem value="baja">Baja</SelectItem>
                            </SelectGroup>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label
                          htmlFor="case-status"
                          className="text-sm font-medium">
                          Estado del Caso
                        </Label>
                        <Select
                          value={additionalData.case_status}
                          onValueChange={(value) =>
                            handleAdditionalDataChange("case_status", value)
                          }>
                          <SelectTrigger id="case-status">
                            <SelectValue placeholder="Seleccione estado" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectGroup>
                              <SelectItem value="iniciado">Iniciado</SelectItem>
                              <SelectItem value="notificado">
                                Notificado
                              </SelectItem>
                              <SelectItem value="contestacion">
                                Contestación
                              </SelectItem>
                              <SelectItem value="pruebas">
                                Etapa de Pruebas
                              </SelectItem>
                              <SelectItem value="alegatos">Alegatos</SelectItem>
                              <SelectItem value="sentencia">
                                Sentencia
                              </SelectItem>
                            </SelectGroup>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label
                          htmlFor="procedural-stage"
                          className="text-sm font-medium">
                          Etapa Procesal
                        </Label>
                        <Select
                          value={additionalData.procedural_stage}
                          onValueChange={(value) =>
                            handleAdditionalDataChange(
                              "procedural_stage",
                              value
                            )
                          }>
                          <SelectTrigger id="procedural-stage">
                            <SelectValue placeholder="Seleccione etapa" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectGroup>
                              <SelectItem value="demanda">Demanda</SelectItem>
                              <SelectItem value="admisión">Admisión</SelectItem>
                              <SelectItem value="notificación">
                                Notificación
                              </SelectItem>
                              <SelectItem value="contestación">
                                Contestación
                              </SelectItem>
                              <SelectItem value="pruebas">Pruebas</SelectItem>
                              <SelectItem value="alegatos">Alegatos</SelectItem>
                              <SelectItem value="sentencia">
                                Sentencia
                              </SelectItem>
                            </SelectGroup>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label
                          htmlFor="estimated-duration"
                          className="text-sm font-medium">
                          Duración Estimada
                        </Label>
                        <Input
                          id="estimated-duration"
                          placeholder="6-12 meses"
                          value={additionalData.estimated_duration}
                          onChange={(e) =>
                            handleAdditionalDataChange(
                              "estimated_duration",
                              e.target.value
                            )
                          }
                        />
                      </div>
                    </div>
                  </div>

                  {/* Case Summary and Arguments */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-primary">
                      Descripción del Caso
                    </h3>
                    <div className="space-y-2">
                      <Label
                        htmlFor="case-summary"
                        className="text-sm font-medium">
                        Resumen del caso *
                      </Label>
                      <Textarea
                        id="case-summary"
                        placeholder="Describa los hechos principales del caso según la normativa colombiana..."
                        className="min-h-[120px]"
                        value={formData.case_summary || ""}
                        onChange={(e) =>
                          handleInputChange("case_summary", e.target.value)
                        }
                      />
                    </div>

                    <div className="space-y-2">
                      <Label
                        htmlFor="key-arguments"
                        className="text-sm font-medium">
                        Argumentos clave
                      </Label>
                      <Textarea
                        id="key-arguments"
                        placeholder="Enumere los principales argumentos legales basados en la legislación colombiana..."
                        className="min-h-[100px]"
                        value={formData.key_arguments || ""}
                        onChange={(e) =>
                          handleInputChange("key_arguments", e.target.value)
                        }
                      />
                    </div>

                    <div className="space-y-2">
                      <Label
                        htmlFor="legal-basis"
                        className="text-sm font-medium">
                        Fundamentos Legales
                      </Label>
                      <Textarea
                        id="legal-basis"
                        placeholder="Artículos de ley, decretos, jurisprudencia aplicable..."
                        className="min-h-[100px]"
                        value={additionalData.legal_basis}
                        onChange={(e) =>
                          handleAdditionalDataChange(
                            "legal_basis",
                            e.target.value
                          )
                        }
                      />
                    </div>
                  </div>

                  {/* Precedents */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-primary">
                      Precedentes Relevantes
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {precedents.map((precedent, index) => (
                        <div key={precedent.id} className="flex gap-2">
                          <Input
                            placeholder="Número de expediente"
                            className="flex-1 font-mono text-sm"
                            value={precedent.expediente}
                            onChange={(e) =>
                              handlePrecedentChange(index, e.target.value)
                            }
                          />
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() =>
                              handleCheckPrecedent(precedent.expediente)
                            }>
                            <PieChart size={16} />
                          </Button>
                        </div>
                      ))}
                    </div>
                    <Button
                      variant="outline"
                      className="w-full mt-1"
                      onClick={addPrecedentField}>
                      + Añadir otro precedente
                    </Button>
                  </div>

                  {/* File Upload Section */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-primary flex items-center gap-2">
                      <Upload className="h-5 w-5" />
                      Documentos y Expedientes
                    </h3>

                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                      <div className="text-center">
                        <Upload className="mx-auto h-12 w-12 text-gray-400" />
                        <div className="mt-4">
                          <Label
                            htmlFor="file-upload"
                            className="cursor-pointer">
                            <span className="mt-2 block text-sm font-medium text-gray-900">
                              Arrastra archivos aquí o haz clic para seleccionar
                            </span>
                            <span className="mt-1 block text-sm text-gray-500">
                              PDF, DOC, DOCX, JPG, PNG hasta 10MB
                            </span>
                          </Label>
                          <Input
                            id="file-upload"
                            type="file"
                            multiple
                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                            className="hidden"
                            onChange={handleFileUpload}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Uploaded Files List */}
                    {uploadedFiles.length > 0 && (
                      <div className="space-y-2">
                        <h4 className="text-sm font-medium">
                          Archivos cargados:
                        </h4>
                        <div className="space-y-2">
                          {uploadedFiles.map((file, index) => (
                            <div
                              key={index}
                              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <div className="flex items-center gap-3">
                                <FileText className="h-5 w-5 text-gray-500" />
                                <div>
                                  <p className="text-sm font-medium">
                                    {file.name}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {formatFileSize(file.size)}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {filePreview[file.name] && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() =>
                                      openDocument(
                                        filePreview[file.name],
                                        file.name
                                      )
                                    }>
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                )}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removeFile(file.name)}>
                                  <Trash2 className="h-4 w-4 text-red-500" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="pt-4 flex justify-end">
                    <Button
                      size="lg"
                      className="gap-2"
                      onClick={handleAnalyzeCase}
                      disabled={isAnalyzing}>
                      {isAnalyzing ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Analizando caso...
                        </>
                      ) : (
                        <>
                          <BarChart3 size={16} />
                          Analizar caso
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Context and Statistics Sidebar */}
            <div className="space-y-6">
              {/* Legal Context Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Scale className="h-5 w-5" />
                    Contexto Jurídico
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <div className="text-sm font-medium">
                      Normativa Aplicable
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Código General del Proceso (Ley 1564/2012)
                    </div>
                  </div>

                  <div>
                    <div className="text-sm font-medium">
                      Precedentes Relevantes
                    </div>
                    <div className="text-sm text-muted-foreground">
                      23 decisiones similares
                    </div>
                  </div>

                  <div>
                    <div className="text-sm font-medium">Juez Asignado</div>
                    <div className="text-sm text-muted-foreground">
                      Dra. María Elena Rodríguez
                    </div>
                  </div>

                  <div>
                    <div className="text-sm font-medium">
                      Tasa Histórica de Éxito
                    </div>
                    <div className="text-sm text-muted-foreground">
                      84% casos del demandante
                    </div>
                  </div>

                  <div>
                    <div className="text-sm font-medium">
                      Tiempo Promedio de Resolución
                    </div>
                    <div className="text-sm text-muted-foreground">
                      8.5 meses
                    </div>
                  </div>

                  <div>
                    <div className="text-sm font-medium">
                      Abogado Demandante
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Dr. Carlos Mendoza (95% tasa éxito)
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Statistics Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">
                    Estadísticas similares
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <div className="text-muted-foreground text-sm mb-1">
                      Casos ejecutivos en Bogotá
                    </div>
                    <div className="flex justify-between items-center">
                      <div className="flex gap-1 items-center">
                        <CheckCircle className="text-green-500" size={16} />
                        <span className="text-sm">
                          73% favorables al demandante
                        </span>
                      </div>
                      <span className="text-sm text-muted-foreground">
                        1,247 casos
                      </span>
                    </div>
                  </div>

                  <div>
                    <div className="text-muted-foreground text-sm mb-1">
                      Tiempo promedio de resolución
                    </div>
                    <div className="font-semibold">7.2 meses</div>
                  </div>

                  <div>
                    <div className="text-muted-foreground text-sm mb-1">
                      Recuperación promedio
                    </div>
                    <div className="font-semibold">85% de la cuantía</div>
                  </div>

                  <div>
                    <div className="text-muted-foreground text-sm mb-1">
                      Tasa de conciliación
                    </div>
                    <div className="font-semibold">
                      32% en audiencia inicial
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Success Factors */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Factores de éxito</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    <li className="flex gap-2 text-sm">
                      <CheckCircle
                        className="text-green-500 shrink-0"
                        size={16}
                      />
                      <span>Documentación completa y en orden</span>
                    </li>
                    <li className="flex gap-2 text-sm">
                      <CheckCircle
                        className="text-green-500 shrink-0"
                        size={16}
                      />
                      <span>Precedente judicial favorable</span>
                    </li>
                    <li className="flex gap-2 text-sm">
                      <CheckCircle
                        className="text-green-500 shrink-0"
                        size={16}
                      />
                      <span>Experiencia del abogado en casos similares</span>
                    </li>
                    <li className="flex gap-2 text-sm">
                      <AlertTriangle
                        className="text-amber-500 shrink-0"
                        size={16}
                      />
                      <span>Situación financiera del deudor</span>
                    </li>
                    <li className="flex gap-2 text-sm">
                      <Ban className="text-red-500 shrink-0" size={16} />
                      <span>Antigüedad de la obligación</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Enhanced Results Section */}
          {showResults && activeAnalysis && (
            <Card className="mt-6" id="results-card">
              <CardHeader>
                <CardTitle className="text-xl flex items-center gap-2">
                  <Target className="h-6 w-6 text-primary" />
                  Resultados de Predicción
                </CardTitle>
                <p className="text-muted-foreground">
                  Basado en análisis de 1.247 casos similares y datos históricos
                  del sistema judicial
                </p>
              </CardHeader>
              <CardContent>
                {/* Main Prediction Metrics */}
                <div className="grid md:grid-cols-3 gap-6 mb-8">
                  <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                    <CardContent className="p-6 text-center">
                      <div className="text-4xl font-bold text-blue-600 mb-2">
                        {activeAnalysis.probability}%
                      </div>
                      <div className="text-sm font-medium text-blue-800">
                        Probabilidad de Éxito
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                    <CardContent className="p-6 text-center">
                      <div className="text-4xl font-bold text-purple-600 mb-2">
                        7.2
                      </div>
                      <div className="text-sm font-medium text-purple-800">
                        Meses Estimados
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                    <CardContent className="p-6 text-center">
                      <div className="text-4xl font-bold text-green-600 mb-2">
                        85%
                      </div>
                      <div className="text-sm font-medium text-green-800">
                        Recuperación Esperada
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Analysis Charts Section */}
                <div className="grid md:grid-cols-2 gap-6 mb-8">
                  {/* Temporal Analysis Chart */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <BarChart3 className="h-5 w-5" />
                        Análisis Temporal de Casos Similares
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-48 bg-muted/30 rounded-lg flex items-center justify-center">
                        <div className="text-center text-muted-foreground">
                          <BarChart3 className="h-12 w-12 mx-auto mb-2" />
                          <p className="text-sm">
                            Gráfico de tendencias de casos exitosos
                          </p>
                          <p className="text-xs">Últimos 12 meses</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Judge Distribution Chart */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <PieChart className="h-5 w-5" />
                        Distribución de Resultados por Juez
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-48 bg-muted/30 rounded-lg flex items-center justify-center">
                        <div className="text-center text-muted-foreground">
                          <PieChart className="h-12 w-12 mx-auto mb-2" />
                          <div className="text-xs space-y-1">
                            <div className="flex items-center gap-2 justify-center">
                              <div className="w-3 h-3 bg-green-500 rounded"></div>
                              <span>A favor Demandante</span>
                            </div>
                            <div className="flex items-center gap-2 justify-center">
                              <div className="w-3 h-3 bg-red-500 rounded"></div>
                              <span>A favor Demandado</span>
                            </div>
                            <div className="flex items-center gap-2 justify-center">
                              <div className="w-3 h-3 bg-orange-500 rounded"></div>
                              <span>Conciliación</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Key Analysis Factors */}
                <div className="grid md:grid-cols-2 gap-6 mb-8">
                  {/* Favorable Factors */}
                  <Card className="border-green-200 bg-green-50">
                    <CardHeader>
                      <CardTitle className="text-lg text-green-800 flex items-center gap-2">
                        <CheckCircle className="h-5 w-5" />
                        Factores Favorables
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex items-start gap-3">
                        <div className="bg-green-100 p-2 rounded-full">
                          <FileText className="h-4 w-4 text-green-600" />
                        </div>
                        <div>
                          <div className="font-medium text-green-800">
                            Documentación Completa
                          </div>
                          <div className="text-sm text-green-600">
                            Títulos y documentos soporte en orden
                          </div>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <div className="bg-green-100 p-2 rounded-full">
                          <Scale className="h-4 w-4 text-green-600" />
                        </div>
                        <div>
                          <div className="font-medium text-green-800">
                            Precedente Judicial
                          </div>
                          <div className="text-sm text-green-600">
                            Jurisprudencia favorable en 18 de 23 casos
                          </div>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <div className="bg-green-100 p-2 rounded-full">
                          <Award className="h-4 w-4 text-green-600" />
                        </div>
                        <div>
                          <div className="font-medium text-green-800">
                            Experiencia del Abogado
                          </div>
                          <div className="text-sm text-green-600">
                            95% tasa de éxito en casos ejecutivos
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Risk Factors */}
                  <Card className="border-red-200 bg-red-50">
                    <CardHeader>
                      <CardTitle className="text-lg text-red-800 flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5" />
                        Factores de Riesgo
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex items-start gap-3">
                        <div className="bg-red-100 p-2 rounded-full">
                          <Ban className="h-4 w-4 text-red-600" />
                        </div>
                        <div>
                          <div className="font-medium text-red-800">
                            Situación Financiera del Deudor
                          </div>
                          <div className="text-sm text-red-600">
                            Reportes CENTRALES muestran sobreendeudamiento
                          </div>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <div className="bg-red-100 p-2 rounded-full">
                          <Clock className="h-4 w-4 text-red-600" />
                        </div>
                        <div>
                          <div className="font-medium text-red-800">
                            Antigüedad de la Obligación
                          </div>
                          <div className="text-sm text-red-600">
                            Desde el 2 años puede generar excepciones
                          </div>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <div className="bg-red-100 p-2 rounded-full">
                          <Calendar className="h-4 w-4 text-red-600" />
                        </div>
                        <div>
                          <div className="font-medium text-red-800">
                            Carga Procesal del Juzgado
                          </div>
                          <div className="text-sm text-red-600">
                            234 procesos activos pueden retrasar el trámite
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Strategic Recommendations */}
                <Card className="bg-gradient-to-r from-pink-50 to-purple-50 border-pink-200 mb-8">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Target className="h-5 w-5 text-pink-600" />
                      Recomendaciones Estratégicas
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <div className="flex items-start gap-3">
                          <div className="bg-pink-100 p-2 rounded-full">
                            <Scale className="h-4 w-4 text-pink-600" />
                          </div>
                          <div>
                            <div className="font-medium text-pink-800">
                              Estrategia Procesal
                            </div>
                            <div className="text-sm text-pink-600">
                              Solicitar medidas cautelares inmediatamente. El
                              análisis muestra 30% de efectividad cuando se
                              implementan en los primeros 30 días.
                            </div>
                          </div>
                        </div>

                        <div className="flex items-start gap-3">
                          <div className="bg-pink-100 p-2 rounded-full">
                            <Clock className="h-4 w-4 text-pink-600" />
                          </div>
                          <div>
                            <div className="font-medium text-pink-800">
                              Gestión de Tiempos
                            </div>
                            <div className="text-sm text-pink-600">
                              Programar estratégicamente primeras horas. La Dra.
                              Rodríguez muestra mejor disposición en audiencias
                              matutinas.
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div className="flex items-start gap-3">
                          <div className="bg-pink-100 p-2 rounded-full">
                            <Award className="h-4 w-4 text-pink-600" />
                          </div>
                          <div>
                            <div className="font-medium text-pink-800">
                              Negociación
                            </div>
                            <div className="text-sm text-pink-600">
                              Considerar acuerdo de pago antes de la primera
                              audiencia. 67% de casos similares se resuelven por
                              conciliación.
                            </div>
                          </div>
                        </div>

                        <div className="flex items-start gap-3">
                          <div className="bg-pink-100 p-2 rounded-full">
                            <BarChart3 className="h-4 w-4 text-pink-600" />
                          </div>
                          <div>
                            <div className="font-medium text-pink-800">
                              Monitoreo
                            </div>
                            <div className="text-sm text-pink-600">
                              Seguimiento quincenal de la situación financiera
                              del deudor mediante reportes CIFIN y DataCrédito.
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Projected Timeline */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Calendar className="h-5 w-5" />
                      Cronograma Proyectado
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex items-center gap-4 p-3 bg-blue-50 rounded-lg border-l-4 border-blue-500">
                        <div className="text-blue-600 font-medium text-sm">
                          Abril 2024
                        </div>
                        <div className="flex-1">
                          <div className="font-medium">
                            Admisión de la Demanda
                          </div>
                          <div className="text-sm text-muted-foreground">
                            Probabilidad: 95%
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-4 p-3 bg-purple-50 rounded-lg border-l-4 border-purple-500">
                        <div className="text-purple-600 font-medium text-sm">
                          Mayo 2024
                        </div>
                        <div className="flex-1">
                          <div className="font-medium">
                            Notificación al Demandado
                          </div>
                          <div className="text-sm text-muted-foreground">
                            Tiempo estimado: 25 días
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-4 p-3 bg-amber-50 rounded-lg border-l-4 border-amber-500">
                        <div className="text-amber-600 font-medium text-sm">
                          Junio 2024
                        </div>
                        <div className="flex-1">
                          <div className="font-medium">
                            Respuesta o Excepciones
                          </div>
                          <div className="text-sm text-muted-foreground">
                            Probabilidad de excepciones: 45%
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-4 p-3 bg-green-50 rounded-lg border-l-4 border-green-500">
                        <div className="text-green-600 font-medium text-sm">
                          Agosto 2024
                        </div>
                        <div className="flex-1">
                          <div className="font-medium">Audiencia Inicial</div>
                          <div className="text-sm text-muted-foreground">
                            Programación estimada
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-4 p-3 bg-emerald-50 rounded-lg border-l-4 border-emerald-500">
                        <div className="text-emerald-600 font-medium text-sm">
                          Octubre 2024
                        </div>
                        <div className="flex-1">
                          <div className="font-medium">
                            Sentencia de Primera Instancia
                          </div>
                          <div className="text-sm text-muted-foreground">
                            Expectativa: Favorable
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <div className="flex justify-end mt-6">
                  <Button className="gap-2" onClick={handleGenerateReport}>
                    <FileText size={16} />
                    Ver informe completo
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="previous">
          <div className="mt-6">
            <div className="mb-4">
              <Input placeholder="Buscar análisis previos..." />
            </div>

            {isLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin" />
              </div>
            ) : previousAnalysis.length === 0 ? (
              <Card className="p-6 text-center">
                <p className="text-muted-foreground">
                  No hay análisis previos guardados.
                </p>
              </Card>
            ) : (
              <div className="space-y-4">
                {previousAnalysis.map((analysis) => (
                  <Card
                    key={analysis.id}
                    className="hover:border-primary transition-colors cursor-pointer">
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-medium text-lg">
                            {analysis.case_type === "labor" && "Caso laboral"}
                            {analysis.case_type === "civil" && "Caso civil"}
                            {analysis.case_type === "corporate" &&
                              "Caso mercantil"}
                            {analysis.case_type === "family" && "Caso familiar"}
                            {analysis.case_type === "administrative" &&
                              "Caso administrativo"}
                            {analysis.case_summary?.length > 20
                              ? ` - ${analysis.case_summary.substring(
                                0,
                                20
                              )}...`
                              : analysis.case_summary
                                ? ` - ${analysis.case_summary}`
                                : ""}
                          </h3>
                          <p className="text-sm text-muted-foreground flex items-center gap-1">
                            <Clock size={14} />
                            {new Date(analysis.created_at!).toLocaleDateString(
                              "es-CO"
                            )}
                          </p>
                        </div>
                        <div
                          className={`text-right ${Number(analysis.probability) >= 70
                              ? "text-green-500"
                              : Number(analysis.probability) <= 40
                                ? "text-red-500"
                                : "text-amber-500"
                            }`}>
                          <div className="text-lg font-bold">
                            {analysis.probability}%
                          </div>
                          <div className="text-sm">
                            {Number(analysis.probability) >= 70
                              ? "Favorable"
                              : Number(analysis.probability) <= 40
                                ? "Desfavorable"
                                : "Incierto"}
                          </div>
                        </div>
                      </div>

                      <div className="flex justify-between mt-4">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={() =>
                            analysis.id && handleDeleteAnalysis(analysis.id)
                          }>
                          Eliminar
                        </Button>
                        <Button
                          variant="outline"
                          className="gap-2"
                          onClick={() => handleViewAnalysis(analysis)}>
                          Ver análisis <ArrowRight size={16} />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Toaster for notifications */}
    </div>
  );
}

export default CasePrediction;
